import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyTable as MatTable } from '@angular/material/legacy-table';
import { MatSort, Sort } from '@angular/material/sort';
import { Source } from 'common/class/data/model/source';
import { User } from 'common/class/user';
import { AWSUserStatus } from 'common/constant/authentication/aws-user-status';
import { MILL_SECONDS } from 'common/constant/general';
import { UserPermissions } from 'common/constant/UserPermissions';
import { Logger } from 'common/utility/logger/logger';
import { Strings } from 'common/utility/string/string';
import { MB_RESPONSE, MB_TYPE, MessageBoxComponent, MessageBoxOptions } from 'components/message-box/message-box.component';
import { ILocale } from 'locale-codes';
import { NgxSpinnerService } from 'ngx-spinner';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, filter, Observable, of, Subject, switchMap, take, takeUntil, timer } from 'rxjs';
import { CognitoService } from 'services/authentication/cognito/cognito.service';
import { EdgeDevice } from 'services/edge-device/edge-device.service';
import { SpinnerTypes } from 'services/loading/spinner/spinner-type';
import { LocaleService } from 'services/locale/locale.service';
import { NotificationService } from 'services/notification/notification.service';
import { OrganizationService } from 'services/organization/organization.service';
import { StorageService } from 'services/storage/storage.service';
import { TranslationService } from 'services/translation/translation.service';
import { UserService } from 'services/user/user.service';
import { UsersDataSource } from './UserDatasource';

const languageSection: string = 'user-maintenance'
const messageBase: string = "user-maintenance"

// Loading timeout
const MAX_TIMEOUT = 60;

enum Mode {
  Add,
  Edit,
  View
}

const controls = {
  email: 'email',
  userName: 'userName',
  lastName: 'lastName',
  firstName: 'firstName',
  locale: 'locale',
  admin: 'admin'
};

const ColumnName = {
  Username: "Username",
  Roles: "Roles",
  FirstName: "FirstName",
  LastName: "LastName",
  Enabled: "Enabled",
  Admin: "Admin",
  Controls: "Controls"
}

const SortOrder = {
  Ascending: "asc",
  Descending: "desc",
  None: Strings.Empty
}

const SpinnerMessages = {
  LoadingUsers: "Loading Users",
  SavingUser: "Saving User",
  SavingEdge: "Saving Edge Devices",
  ResendPassword: "Resending Password",
  Updating: "Updating",
  Loading: "Loading"

}



@Component({
  selector: 'ems-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss']
})
export class UserComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild(MatSort) sort?: MatSort

  @ViewChild(MatTable) table?: MatTable<any>;

  private unsubscribe: Subject<void> = new Subject()

  private selectedUser: User = {}
  private mode: Mode = Mode.View
  private dataSource: UsersDataSource
  private users: Array<User> = []
  private errors: Array<string> = []
  private loggedOnUser: User | undefined = undefined
  private edgeDevices: Array<any> = []
  private spinnerMessage: string = ""



  private selectedEdgeDevicesChanged: Subject<Array<any>> = new Subject<Array<any>>()
  private pendingEdgeDeviceIds: Array<string> | null = null


  selectedEdgeDevices: Array<any> = []
  edgeDevicesChanged: boolean = false

  selectionDefaults: Array<any> = []


  public form: FormGroup = this.createForm();
  public sourcesForm: FormGroup


  public get ColumnName(): typeof ColumnName {
    return ColumnName
  }

  public get Title(): string {
    Logger.debug("Title: Checking Mode ", this.Mode)
    const key: string = this.Mode === Mode.Add ? 'add' : 'edit'
    return `${languageSection}.form-title.${key}`
  }

  public get SourceTitle(): string {
    return `${languageSection}.form-title.sources`
  }

  public get EdgeDeviceTitle(): string {
    return `${languageSection}.form-title.edge-devices`
  }

  public get Sources(): FormArray {
    return this.sourcesForm.controls['sources'] as FormArray
  }

  public get Maintaining(): boolean {
    Logger.debug("Maintaining: Checking Mode ", this.Mode)
    return this.Mode === Mode.Add || this.Mode == Mode.Edit
  }

  public get Editing(): boolean {
    Logger.debug("Editing: Checking Mode ", this.Mode)
    return this.Mode === Mode.Edit
  }

  public get Viewing(): boolean {
    Logger.debug("Viewing: Checking Mode ", this.Mode)
    return this.Mode === Mode.View
  }

  public get Mode(): Mode {
    return this.mode
  }

  private set Mode(mode: Mode) {
    Logger.debug("Mode: Setting Mode ", mode)
    this.mode = mode
  }

  public get DataSource(): UsersDataSource {
    return this.dataSource
  }

  public get Columns(): Array<string> {
    return [ColumnName.Username, ColumnName.Roles, ColumnName.FirstName, ColumnName.LastName, ColumnName.Enabled, ColumnName.Controls];
  }

  public get disableSave(): boolean {
    return this.invalidForms || (this.pristineForms && this.edgeDeviceSelectionUnchanged())
  }

  public get showPasswordResetButton(): boolean {
    return !this.showPasswordResendButton
  }

  public get showPasswordResendButton(): boolean {
    return !!this.userSelected && this.selectedUser.Status === AWSUserStatus.TemporaryPassword
  }

  public get disableResetPassword(): boolean {
    return !this.userService.hasPermission(UserPermissions.PasswordReset)
  }

  public get User(): User {
    return this.selectedUser
  }

  private get invalidForms(): boolean {
    return this.form.invalid || this.sourcesForm.invalid
  }

  private get pristineForms(): boolean {
    return this.form.pristine && this.sourcesForm.pristine
  }

  public get email(): FormControl {
    return this.getFormControl(controls.email)
  }

  public get firstName(): FormControl {
    return this.getFormControl(controls.firstName)
  }

  public get lastName(): FormControl {
    return this.getFormControl(controls.lastName)
  }

  public get userName(): FormControl {
    return this.getFormControl(controls.userName)
  }

  public get locale(): FormControl {
    return this.getFormControl(controls.locale)
  }

  public get admin(): FormControl {
    return this.getFormControl(controls.admin)
  }

  public get Errors(): Array<string> {
    return this.errors
  }

  public get AnyErrors(): boolean {
    return !!this.Errors.length
  }

  public get DefaultSaveUserError(): string {
    return this.getError('default-save-user')
  }

  public get DefaultError(): string {
    return this.getError('default')
  }

  public get Adding(): boolean {
    Logger.debug("Adding: Checking Mode ", this.Mode)
    return this.Mode === Mode.Add
  }

  public get spinnerType(): string {
    return SpinnerTypes.BallClipRotate;
  }

  public get spinnerName(): string {
    return "userManagement";
  }

  public get SelectedLocale(): ILocale {
    if (!!this.locale.value) {
      const localeByTag = this.localeService.getLocaleByTag(this.locale.value)
      return localeByTag ?? this.DefaultLocale
    }
    return this.DefaultLocale
  }


  public set SelectedLocale(locale: ILocale) {
    this.locale.setValue(locale)
  }

  public get EdgeDevices(): any {
    return this.edgeDevices
  }

  private selectorItemsCache?: { source: Array<any>, items: Array<any> }

  public get SelectorItems(): any {
    if (this.selectorItemsCache?.source !== this.edgeDevices) {
      this.selectorItemsCache = { source: this.edgeDevices, items: this.edgeDevicesToSelectorItems(this.edgeDevices) }
    }
    return this.selectorItemsCache.items
  }

  public get SpinnerMessage(): string {
    // 'user-maintenance.messages.loading-users' | translate
    return !!this.spinnerMessage ? this.spinnerMessage : SpinnerMessages.LoadingUsers
  }

  public loggedOnUserSelected(user?: User): boolean {
    user = user ?? this.selectedUser
    return user.Id === this.LoggedOnUser?.Id
  }

  public userRole(user: User): string {
    const key = !!user.Admin ? "admin" : "view"
    return `${languageSection}.Roles.${key}`
  }

  public RowSelected(user: User): boolean {
    return !!this.selectedUser && this.selectedUser.Id === user?.Id
  }

  public add(): void {
    this.form.reset()
    const user = new User()
    user.Email = user.FirstName = user.LastName = user.Id = user.Username = undefined
    user.Admin = false
    user.Locale = this.SelectedLocale.tag
    this.locale.setValue(user.Locale)
    this.selectedUser = user
    this.selectedEdgeDevices = []
    this.clearUserSources()
    Logger.debug("add(): Setting mode", Mode.Add)

    this.Mode = Mode.Add
    Logger.debug("Adding user: SelectedLocale", this.SelectedLocale)
  }

  public edit(user: User): void {

    Logger.debug("Editing user ", user)

    this.selectedUser = user
    this.email.setValue(this.selectedUser.Email)
    this.userName.setValue(this.selectedUser.Username)
    this.firstName.setValue(this.selectedUser.FirstName)
    this.lastName.setValue(this.selectedUser.LastName)
    this.admin.setValue(this.selectedUser.Admin)
    this.loggedOnUserSelected() ? this.admin.disable() : this.admin.enable()
    // this.getUserSources(this.selectedUser.Id)
    this.locale.setValue(this.selectedUser.Locale ?? this.SelectedLocale)
    this.edgeDevicesChanged = false
    this.selectedEdgeDevices = []

    this.userService.getEdgeDevices(this.selectedUser.Id!).pipe(
      take(1),
      takeUntil(this.unsubscribe),
      catchError(() => of([]))
    ).subscribe((devices: EdgeDevice[]) => {
      this.selectedEdgeDevices = devices.map(device => device.edgeId)
      this.mergeEdgeDevices(devices)
    })

    this.Mode = Mode.Edit
    this.setFormsPristine()
  }

  public resetPassword(user: User, event: MouseEvent): void {
    event.stopPropagation();
    if (!user?.Username) return
    const username: string = user.Username ?? Strings.Empty

    this.resetPasswordDialog(username)
      .pipe(take(1))
      .subscribe((submitReset: Boolean) => {
        if (submitReset) {

          this.cognitoService.poolIdPasswordVerify(username)
            .subscribe((result: any) => {
              if (!result) {
                Logger.error(`Error requesting password reset for user: ${username}`)
              }
            })
        }
      })
  }

  public resendPassword(): void {
    if (!this.selectedUser?.Username) return

    this.resendPasswordDialog()
      .pipe(take(1))
      .subscribe((resend: Boolean) => {
        if (resend) {
          this.startSpinner(SpinnerMessages.ResendPassword)
          const username: string = this.selectedUser.Username ?? Strings.Empty
          this.cognitoService.resendTemporaryPassword(username)
            .pipe(
              catchError((response: HttpErrorResponse) => {
                this.stopSpinner()
                this.showErrors([this.getError('resend-password')])
                Logger.error(`Unable to request users temporary password: ${response.error}`)
                return of(response.error)
              }),
              take(1)
            )
            .subscribe((result: any) => {
              this.stopSpinner()
              Logger.debug(`resendPassword result: ${result}`)
            })
        }
      })
  }

  public save(spinner: boolean = true, complete: boolean = true): void {

    spinner && this.startSpinner(SpinnerMessages.SavingUser)
    this.clearErrors()
    Logger.debug(`Saving user ${this.userName.value}`)

    const userInfo: Observable<any> = this.SaveUserForm ? this.saveUserInfo() : of(`success:${this.selectedUser.Id}`)
    // const userSources: Observable<any> = this.SaveSourceListForm ? this.saveUserSources() : of(null)

    this.saveEdgeDevices()

    userInfo.pipe(
      take(1),
      switchMap((result) => {
        Logger.debug(`Save user result? ${result}`)

        this.notificationService.notify({ message: `Saved` })
        this.userService.refreshUsers().subscribe()
        let returnValue = of(null)

        Logger.debug(`Saving Sources? ${this.SaveSourceListForm}`)
        if (!this.SaveSourceListForm) return returnValue

        // const parsedResult: { success: boolean, userId: string } = this.parseSaveResult(result)
        // if (parsedResult.success) {

        //   Logger.debug(`Successfully Saved User ${parsedResult.userId}`)
        //   this.selectedUser.Id = this.selectedUser.Id ?? parsedResult.userId
        //   returnValue = this.saveUserSources()
        // }

        return returnValue

      })
    ).subscribe(() => {
      spinner && this.stopSpinner()
      complete && this.completeEdit()
    })
  }

  public toggleUserState(user: User, event: MouseEvent): void {
    event.stopPropagation()
    if (this.loggedOnUserSelected(user)) {
      return
    }
    Logger.debug(`Toggling Enabled State for: ${user.Username}`)

    this.startSpinner(SpinnerMessages.Updating)
    if (!user?.Id) return
    this.userService.toggleState(user.Id, !user.Enabled)
      .pipe(catchError((error) => {
        Logger.error(`Error toggling state for user: ${user?.Id}. Error: ${error.error}`)
        this.stopSpinner()
        return of(null)
      }))
      .subscribe((result: any) => {
        this.stopSpinner()
        Logger.debug(`Result: ${result}`)
        this.dataSource.loadUsers()
      })
  }

  public cancel(): void {
    this.returnToList()
  }

  public buttonLabel(key: string): string {
    return `global.button.${key}`
  }

  public tableHeader(key: string): string {
    return `${languageSection}.table-headers.${key}`
  }

  public placeholder(key: string): string {
    return `${languageSection}.placeholders.${key}`
  }

  public sourceName(index: number): string {
    const displayNames: { [key: string]: any } = this.organizationService.Sources[index].displayNames
    const countryCode: string = TranslationService.CurrentLanguage.CountryCode
    return displayNames[countryCode]
  }

  public LocaleChanged(locale: ILocale): void {
    this.locale.setValue(locale.tag)
    Logger.debug(`Locale changed:`, locale)
    this.form.markAsDirty()
    this.form.markAsTouched()
  }

  onSelectionChange(selected: string[]) {
    this.edgeDevicesChanged = true
    Logger.debug('Selected IDs:', selected)
    this.pendingEdgeDeviceIds = selected
    this.selectedEdgeDevicesChanged.next(selected)
  }

  constructor(
    private userService: UserService,
    private organizationService: OrganizationService,
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private cognitoService: CognitoService,
    private spinner: NgxSpinnerService,
    private localeService: LocaleService,
    private translationService: TranslationService,
    private notificationService: NotificationService,

  ) {
    this.dataSource = new UsersDataSource(this.userService)
    this.dataSource.loadUsers()

    this.cacheUsers()
    this.sourcesForm = this.createSourcesForm()
    this.getEdgeDeviceList()
    this.autoUpdateUser()
  }

  ngOnInit(): void {
    this.showWaitSpinner()
  }

  ngAfterViewInit(): void {
    this.columnSortChanges()
  }

  ngOnDestroy(): void {
    this.stopSpinner()
    if (this.pendingEdgeDeviceIds) {
      this.saveUserEdgeDevices(this.pendingEdgeDeviceIds)
    }
    this.unsubscribe.next()
    this.unsubscribe.complete()
  }

  private get userSelected(): boolean {
    return !!this.User?.Id
  }

  private get LoggedOnUser(): User {
    this.loggedOnUser = this.loggedOnUser ?? StorageService.loggedOnUser
    return this.loggedOnUser
  }
  private get SaveUserForm(): boolean {
    return this.form.dirty && this.form.valid
  }

  private get SaveSourceListForm(): boolean {
    return this.sourcesForm.dirty && this.sourcesForm.valid && this.form.valid
  }

  private get DefaultLocale(): ILocale {
    return this.localeService.DefaultLocale
  }

  private returnToList(): void {
    this.clearErrors()
    this.Mode = Mode.View
    this.table?.renderRows()
  }

  private createForm(): FormGroup {
    return this.formBuilder.group({
      email: [Strings.Empty, [Validators.required]],
      userName: [Strings.Empty, [Validators.required]],
      lastName: [Strings.Empty, [Validators.required]],
      firstName: [Strings.Empty, [Validators.required]],
      locale: [this.DefaultLocale.tag, [Validators.required]],
      admin: [false, []],
    });
  }

  private getFormControl(controlName: string): FormControl {
    return this.form.controls[controlName] as FormControl;
  }

  private showWaitSpinner(): void {
    this.dataSource.Loading.pipe(
      takeUntil(this.unsubscribe)
    ).subscribe(loading => {
      loading ? this.spinner.show(this.spinnerName) : this.spinner.hide(this.spinnerName)
    })
  }

  private columnSortChanges(): void {
    this.sort?.sortChange.pipe(
      takeUntil(this.unsubscribe)
    ).subscribe((change: Sort) => {
      Logger.debug(`Sort change. Column: '${change.active}', Direction: '${change.direction}'`)
      this.dataSource.sorted(this.sortUsers(change.active, change.direction))
    })
  }

  private cacheUsers(): void {
    this.dataSource.Users
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((users: Array<User>) => {
        this.users = users
      })
  }

  private sortUsers(column: string, direction: string) {
    if (!direction) {
      return this.users
    }

    return this.users.sort((user1: User, user2: User) => {
      let candidate1: string | undefined
      let candidate2: string | undefined

      switch (column) {
        case ColumnName.Username:
          candidate1 = user1.Username
          candidate2 = user2.Username
          break

        case ColumnName.FirstName:
          candidate1 = user1.FirstName
          candidate2 = user2.FirstName
          break

        case ColumnName.LastName:
          candidate1 = user1.LastName
          candidate2 = user2.LastName
          break

        case ColumnName.Enabled:
          candidate1 = user1.Enabled ? '1' : '0'
          candidate2 = user2.Enabled ? '1' : '0'
          break

        case ColumnName.Roles:
          candidate1 = user1.Admin ? '1' : '0'
          candidate2 = user2.Admin ? '1' : '0'
          break

      }
      candidate1 = candidate1 ?? Strings.Empty
      candidate2 = candidate2 ?? Strings.Empty

      const returnValue = direction === SortOrder.Ascending
        ? candidate1 > candidate2 ? 1 : candidate2 > candidate1 ? -1 : 0
        : candidate1 < candidate2 ? 1 : candidate2 < candidate1 ? -1 : 0

      return returnValue
    })
  }

  private resetPasswordDialog(username: string): Observable<any> {
    return combineLatest([
      this.translationService.get(this.resetMessage("title")),
      this.translationService.get(this.resetMessage("body"))]).
      pipe(
        takeUntil(this.unsubscribe),
        filter(([title, body]) => title && body),
        switchMap(([title, body]) => {
          const message: string = body
          message.replace("<user>", username)
          return this.confirmationDialog(title, message.replace("<user>", username))
        })
      )
  }

  private resendPasswordDialog(): Observable<boolean> {
    const title: string = this.resendMessage("title")
    const message: string = this.resendMessage("body")

    return this.confirmationDialog(title, message)
  }

  private confirmationDialog(title: string, message: string): Observable<boolean> {
    this.clearErrors()
    const msgBoxSettings: MessageBoxOptions = {
      type: MB_TYPE.YES_NO,
      title: title,
      message: message
    }

    const msgBox: MatDialogRef<MessageBoxComponent, any> = this.dialog.open(MessageBoxComponent, {
      data: msgBoxSettings
    });

    return msgBox.afterClosed().pipe(
      take(1),
      switchMap(result => of(result.event === MB_RESPONSE.YES))
    )
  }

  private resetMessage(key: string): string {
    return this.dialogMessageKey('reset-password', key)
  }

  private resendMessage(key: string): string {
    return this.dialogMessageKey('resend-password', key)
  }

  private dialogMessageKey(dialogKey: string, messageKey: string): string {
    return `${messageBase}.dialog.${dialogKey}.${messageKey}`
  }

  private getError(key: string): string {
    return `${messageBase}.messages.errors.${key}`
  }

  private clearErrors(): void {
    this.errors = []
  }

  private showErrors(errors: Array<string>): void {
    this.errors = errors
    timer(3000).pipe(take(1)).subscribe(() => this.clearErrors())
  }

  private completeEdit(): void {
    this.stopSpinner()
    if (!this.AnyErrors) {
      this.dataSource.loadUsers()
      this.returnToList()
    }
  }

  private loadingId = -1
  private loadingTimeout = MAX_TIMEOUT

  private startSpinner(message: string = SpinnerMessages.LoadingUsers): void {

    this.spinnerMessage = message
    this.spinner.show(this.spinnerName)
    this.loadingTimeout = MAX_TIMEOUT
    if (this.loadingId !== -1) {
      window.clearInterval(this.loadingId)
    }
    this.loadingId = window.setInterval(() => {
      if (this.loadingTimeout && !--this.loadingTimeout) {
        this.spinner.hide(this.spinnerName)
        // Tell the user we timed out
        const msgBoxSettings: MessageBoxOptions = {
          type: MB_TYPE.YES_NO,
          title: undefined,
          message: "user-maintenance.messages.loading-timeout"
        }
        const msgBox = this.dialog.open(MessageBoxComponent, {
          data: msgBoxSettings
        });
        msgBox.afterClosed().subscribe((res) => {
          if (res?.event === MB_RESPONSE.YES && this.loadingId != -1) {
            this.loadingTimeout = MAX_TIMEOUT
            this.spinner.show(this.spinnerName)
          } else {
            this.spinner.hide(this.spinnerName)
            window.clearInterval(this.loadingId)
            this.loadingId = -1
          }
        }); // afterClosed subscribe
      }
    }, MILL_SECONDS) // setInterval
  } // startSpinner()

  private stopSpinner(): void {
    this.spinner.hide(this.spinnerName);
    window.clearInterval(this.loadingId)
    this.loadingId = -1
  }

  private clearUserSources(): void {
    this.setSourceControls([])
  }

  // private getUserSources(id?: string): void {
  //   if (!id) return
  //   this.startSpinner(SpinnerMessages.Loading)
  //   this.userService.sources(id).pipe(
  //     take(1),
  //     catchError((error) => {
  //       Logger.error(`Error loading sources for user ${id}: ${error}`)
  //       return of([])
  //     }),
  //     finalize(() => this.stopSpinner())
  //   )
  //     .subscribe(userSources => {
  //       Logger.debug(`Got Sources for user ${this.selectedUser.Username} ${userSources}`)
  //       this.setSourceControls(userSources)
  //     })
  // }

  private setSourceControls(userSources: Array<string>): void {
    this.organizationService.Sources.forEach((source: Source, index: number) => {
      const control: FormControl = this.Sources.controls[index] as FormControl
      control.setValue(userSources.some((userSource: string) => userSource == source.Name))
    })
  }

  private createSourcesForm(): FormGroup {
    const controls = this.formBuilder.array([])
    this.organizationService.Sources.forEach(() => {
      controls.push(new FormControl())
    })

    return this.formBuilder.group({
      sources: controls
    })
  }

  private setFormsPristine(): void {
    this.form.markAsPristine()
    this.sourcesForm.markAsPristine()
  }

  private saveUserInfo(): Observable<any> {
    let returnValue: Observable<any> = of(null)

    Logger.debug("saveUserInfo: Checking Mode ", this.Mode)
    if (this.Mode == Mode.Add) {
      returnValue = this.userService.create(this.userName.value, this.email.value, this.firstName.value, this.lastName.value, this.admin.value, this.locale.value)
        .pipe(catchError((error) => {
          this.stopSpinner()
          this.errors.push(error?.code)
          return of(null)
        }))
    }
    else {
      if (!!this.selectedUser?.Id) {
        returnValue = this.userService.update(this.selectedUser.Id, this.firstName.value, this.lastName.value, this.admin.value, this.email.value, this.locale.value)
          .pipe(catchError((error) => {
            this.stopSpinner()
            this.errors.push(error?.code)
            return of(null)
          }))
      }
    }
    return returnValue
  }

  // private saveUserSources(): Observable<any> {
  //   let returnValue: Observable<any> = of(null)
  //   if (!!this.selectedUser.Id) {
  //     const selectedSources: Array<string> = this.getSelectedSources()
  //     returnValue = this.userService.sourcesSet(this.selectedUser.Id, selectedSources)
  //   }
  //   return returnValue
  // }

  private getSelectedSources(): Array<string> {
    const returnValue: Array<string> = []
    this.Sources.controls.forEach((control, index) => {
      if (control.value) {
        returnValue.push(this.organizationService.Sources[index].Name)
      }
    })
    return returnValue
  }

  private parseSaveResult(result: string): { success: boolean, userId: string } {
    let returnValue = { success: false, userId: Strings.Empty }
    if (!result) return returnValue

    const parts = result.split(':')
    if (parts.length < 2) return returnValue

    const success = parts[0] == 'success'
    const id: string = parts[1]


    return { success: success, userId: id }
  }

  private getUserEdgeDevices(userId?: string): any {

    const result = this.selectionDefaults.find(user => user.id == userId)
    return result?.items
  }

  private saveEdgeDevices(): void {
    const result = this.selectionDefaults.find(user => user.id == this.selectedUser.Id)
    if (result) {
      result.items = [...this.selectedEdgeDevices]
    }
  }

  private edgeDeviceSelectionUnchanged(): boolean {

    const userEdgeDevices = this.getUserEdgeDevices(this.selectedUser.Id)

    const returnValue = !!userEdgeDevices && [...userEdgeDevices].sort().join(',') === [...this.selectedEdgeDevices].sort().join(',')
    return returnValue
  }

  private autoUpdateUser(): void {
    this.form.valueChanges.pipe(
      filter(_ => this.Editing && this.SaveUserForm),
      debounceTime(1000),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe),
    ).subscribe(form => {
      Logger.debug(`Auto saving user: ${form.userName}`, form)
      // this.save(false, false)
      this.save(true, false)
    })

    this.selectedEdgeDevicesChanged
      .pipe(
        debounceTime(3000),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.unsubscribe),
      ).subscribe(selected => {
        this.saveUserEdgeDevices(selected)
      })
  }

  private saveUserEdgeDevices(selected: Array<string>): void {
    if (!this.selectedUser?.Id) return

    this.userService.setEdgeDevices(this.selectedUser.Id, selected)
      .pipe(
        take(1),
        catchError((error) => {
          Logger.error(`Error saving Edge devices? ${error?.error}`)
          this.notificationService.notify({ message: `Failed to save edge devices`, notificationType: 'error' })
          return of(null)
        })
      ).subscribe((result) => {
        if (!result) return
        this.pendingEdgeDeviceIds = null
        Logger.debug(`Save Edge devices? ${result}`)
        this.notificationService.notify({ message: `Saved` })
      })
  }


  private getEdgeDeviceList(): void {
    this.organizationService.EdgeDevicesChanged
      .pipe(
        takeUntil(this.unsubscribe)
      ).subscribe(devices => {
        this.edgeDevices = devices
      })

    this.organizationService.getEdgeDevices(this.organizationService.Identifier)
      .pipe(
        take(1),
        takeUntil(this.unsubscribe),
        catchError(() => of([]))
      ).subscribe()
  }


  private edgeDevicesToSelectorItems(edgeDevices: any[]): any[] {
    if (!edgeDevices) return []
    return edgeDevices.map(device => {
      return { id: device.edgeId, name: device.label || device.edgeId }
    })
  }

  private mergeEdgeDevices(devices: EdgeDevice[]): void {
    const missing = devices.filter(device => !this.edgeDevices.some(existing => existing.edgeId === device.edgeId))
    if (missing.length) {
      this.edgeDevices = [...this.edgeDevices, ...missing]
    }
  }

  private selectorItemsToEdgeDevices(selectorItems: any[]): any[] {
    if (!selectorItems) return []
    return selectorItems.map(item => {
      return { edgeId: item.edgeId, label: item.label }
    })

  }

  // private setUserEdgeDeviceSelection(): void {
  //   if (!this.selectedUser?.Id) return
  //   this.userService.sources(this.selectedUser.Id!)
  //     .pipe(
  //       take(1)

  //     ).subscribe(result => {
  //       this.selectedEdgeDevices = result ?? []
  //     })
  // }

}
